// Deterministic "nearby area" picker for programmatic matrix pages.
// Same output on every build (no randomness) so internal-link graph is stable.

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

/**
 * Picks `count` sibling items, preferring the same group (zone / region) and
 * rotating the start index by the current slug so link distribution is spread
 * evenly across the matrix instead of always hitting the first few pages.
 */
export function pickNearby<T extends { slug: string }>(
  all: readonly T[],
  current: T,
  group: (t: T) => string,
  count = 4,
): T[] {
  const g = group(current);
  const same = all.filter((t) => t.slug !== current.slug && group(t) === g);
  const rest = all.filter((t) => t.slug !== current.slug && group(t) !== g);
  const pool = [...same, ...rest];
  if (pool.length === 0) return [];
  const start = hash(current.slug) % pool.length;
  const n = Math.min(count, pool.length);
  return Array.from({ length: n }, (_, i) => pool[(start + i) % pool.length]);
}

/**
 * Same as pickNearby, but prepends the two list-adjacent siblings (previous and
 * next, wrapping around). Adjacency is symmetric, so every item in the list is
 * guaranteed at least two reciprocal inbound links — no thin or orphan hubs.
 */
export function pickReciprocal<T extends { slug: string }>(
  all: readonly T[],
  current: T,
  group: (t: T) => string,
  count = 5,
): T[] {
  if (all.length < 2) return [];
  const i = all.findIndex((t) => t.slug === current.slug);
  const adjacent =
    i < 0
      ? []
      : [all[(i - 1 + all.length) % all.length], all[(i + 1) % all.length]].filter(
          (t): t is T => Boolean(t) && t.slug !== current.slug,
        );
  const seen = new Set<string>();
  const out: T[] = [];
  for (const t of [...adjacent, ...pickNearby(all, current, group, count)]) {
    if (seen.has(t.slug)) continue;
    seen.add(t.slug);
    out.push(t);
  }
  return out.slice(0, Math.max(count, adjacent.length));
}
